import { OrganigramComponent } from './../organigram-component';
import { NodeGroup } from './../layout/node-group';
import { EmployeeDataType } from './../../../../data-management/data-sources/sources/employees-data-source';
import { Gui } from './../../../gui';
import { SelectUserCommandParameters } from './../../../commands/select-user-command-executor';
import { ColorConfig } from './../../../color-config';
import { VisualNode } from './../visual/visual-node';
import { Colors } from '@/util/colors';

export abstract class EmployeeNode extends VisualNode {
    public selected: boolean = false;
    public enabled: boolean = false;
    protected svgSymbol: any;
    protected fill: string;
    protected stroke: string;
    protected strokeWidth: number = 0.6;
    private svgText: any;
    private svgTextBg: any;
    private textBoxMargin: number = 5;

    constructor(
        public data: EmployeeDataType,
        protected organigram: OrganigramComponent,
        protected gui: Gui,
        nodeGroup: NodeGroup = null
    ) {
        super(nodeGroup ? nodeGroup : new NodeGroup());

        this.gui.commandManager.selectUserCommandExecutor.commandExecuted.attach(
            (parameters: SelectUserCommandParameters) => {
                this.checkSelected(parameters);
            }
        );

        this.gui.commandManager.selectAccountCommandExecutor.commandExecuted.attach(() => {
            if (this.selected) {
                this.deselect();
            }
        });

        requestAnimationFrame(this.drawTextBox);
    }

    private bringTooltipToFront = () => {
        if (!this.svgText || !this.svgTextBg) { return; }

        // Ensure layer itself is top-most
        this.organigram.ensureTooltipLayerOnTop();

        // Ensure bg then text ordering inside the layer
        this.svgTextBg.appendTo(this.organigram.tooltipLayer);
        this.svgText.appendTo(this.organigram.tooltipLayer);
    }


    protected drawTextBox = () => {
        const fontSize = Math.max(this.layoutNode.pixelR);
        this.svgTextBg = this.organigram.tooltipLayer.rect(
            this.layoutNode.pixelX + this.layoutNode.pixelR + this.textBoxMargin,
            this.layoutNode.pixelY - this.layoutNode.pixelR / 2 - this.textBoxMargin,
            0,
            fontSize + this.textBoxMargin,
            5
        );
        this.svgTextBg.attr({
            'pointer-events': 'none'
        });
        this.drawText();
    }

    protected repositionText = () => {
        if (this.svgText) {
            this.svgText.attr({
                x: this.layoutNode.pixelX + this.layoutNode.pixelR + this.textBoxMargin * 2,
                y: this.layoutNode.pixelY + this.layoutNode.pixelR / 8,
                'font-size': Math.max(this.layoutNode.pixelR)
            });
        }
        if (this.svgTextBg) {
            this.svgTextBg.attr({
                x: this.layoutNode.pixelX + this.layoutNode.pixelR + this.textBoxMargin,
                y: this.layoutNode.pixelY - this.layoutNode.pixelR / 2 - this.textBoxMargin,
                width: this.svgText.node.getBoundingClientRect().width + this.textBoxMargin * 2
            });
        }
    }

    private drawText = () => {
        this.svgText = this.organigram.tooltipLayer.text(
            this.layoutNode.pixelX + this.layoutNode.pixelR + this.textBoxMargin * 2,
            this.layoutNode.pixelY + this.layoutNode.pixelR / 8,
            this.data.Name
        );
        this.svgText.attr({
            fill: ColorConfig.organigramConsultantName,
            'font-size': Math.max(this.layoutNode.pixelR),
            'font-weight': '900',
            'pointer-events': 'none',
            strokeWidth: 1
        });
        this.disableText();
    }

    public enableText = () => {
        if (!this.svgText) {
            return;
        }
        this.svgText.attr({
            fill: ColorConfig.organigramConsultantName,
            'font-weight': 700
        });
        this.repositionText();
        if (this.selected) {
            this.svgTextBg.attr({
                fill: ColorConfig.organigramConsultantSelected,
                stroke: ColorConfig.organigramConsultantNameBorder,
                strokeWidth: 1
            });
        } else {
            this.svgTextBg.attr({
                fill: ColorConfig.organigramConsultantNameBackground,
                stroke: ColorConfig.organigramConsultantNameBorder,
                strokeWidth: 1
            });
        }
        this.bringTooltipToFront();
    }

    public disableText = () => {
        // this.svgSymbol.attr({
        //     "stroke": ColorConfig.hidden,
        //     "strokeWidth": 0
        // })
        this.svgText.attr({
            fill: ColorConfig.hidden,
            strokeWidth: 0,
            'font-size': 1
        });
        this.svgTextBg.attr({
            width: 0,
            fill: 'rgba(255,255,255, 0)'
        });
    }

    protected abstract initdraw(): void;

    protected abstract redraw(): void;

    protected handleClick = (e: any) => {
        this.gui.commandManager.selectUserCommandExecutor.requestExecute({
            userId: this.data._internal_datasource_id_
        });
        e.stopPropagation();
    }

    public enable() {
        if (!this.selected) {
            this.setInFilterSelection();
        }
        this.enabled = true;
    }

    public disable() {
        if (!this.selected) {
            this.setDeselected();
        }
        this.enabled = false;
    }

    private checkSelected(parameters: SelectUserCommandParameters) {
        if (parameters.userId === this.data._internal_datasource_id_) {
            this.select();
        } else {
            if (this.selected) {
                this.deselect();
            }
        }
    }

    private select() {
        this.setSelected();
        this.selected = true;
        this.enableText();
    }

    private deselect() {
        this.disableText();
        if (this.enabled) {
            this.setDeselected();
            this.setInFilterSelection();
        } else {
            this.setDeselected();
        }
        this.selected = false;
    }

    protected setColors(fill: string, stroke: string) {
        this.fill = fill;
        this.stroke = stroke;
        this.refreshColors();
    }

    protected setDeselected() {
        this.setColors(ColorConfig.organigramConsultantNormal, ColorConfig.organigramConsultantNormalBorder);
    }

    protected setHovered() {
        this.setColors(this.fill, ColorConfig.organigramConsultantSelectedBorder);
        this.enableText();
    }

    protected handleHover() {
        this.setHovered();
    }

    protected handleOut() {
        this.setOut();
    }

    protected setOut() {
        if (this.selected) {
            this.setSelected();
        } else {
            if (this.enabled) {
                this.setInFilterSelection();
            } else {
                this.setDeselected();
            }
            this.disableText();
        }
    }

    protected isFutureEmployee(): boolean {
        return !this.data.StartDate || this.data.StartDate > new Date();
    }

    protected setInFilterSelection() {
        let color = ColorConfig.organigramConsultantEnabled;

        if (this.isFutureEmployee()) {
            color = Colors.shadeColor(color, ColorConfig.futureEmployeeShade);
        }

        this.setColors(color, ColorConfig.organigramConsultantEnabledBorder);
    }

    protected abstract refreshColors(): void;

    protected abstract setSelected(): void;
}
